import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation} from '@angular/core';
import {BehaviorSubject, forkJoin, lastValueFrom, Observable, Subscription} from 'rxjs';
import {AppConfigService} from '@service/config/app-config.service';
import {
    AddressModel,
    ApplicantModel,
    ApplicationModel,
    BusinessOwnerModel,
    ConsentModel,
    CustomerConsentModel,
    EmploymentModel,
    FinanceModel,
    KeyIdentifierModel,
    ResidenceModel
} from '@service/models';
import {Title} from '@angular/platform-browser';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {ApplicantService, ApplicationService} from '@service/services';
import {ApplicantType} from '@service/enum/process-state.enum';
import {ProgressButtonOptions} from '@service/models/interfaces/progress-button.interface';
import {IdentityInfoComponent} from './identity-info/identity-info.component';
import {AdditionalInfoComponent} from './additional-info/additional-info.component';
import {ReferenceApplicantComponent} from './reference-applicant/reference-applicant.component';
import {MatStepper} from '@angular/material/stepper';
import {EmploymentInfoComponent} from './employment-info/employment-info.component';
import {ResidenceInfoComponent} from './residence-info/residence-info.component';
import {AuthorizedUserComponent} from './authorized-user/authorized-user.component';
import {DatePipe, UpperCasePipe} from '@angular/common';
import {BeneficiaryComponent} from './beneficiary/beneficiary.component';
import {ReferenceApplicantModel} from '@service/models/applicant.model';
import {ConsentEnum, Event} from '@service/enum/consent-enum';
import {MatDialog} from '@angular/material/dialog';
import {MessageModelComponent} from '@core/_shared/message-model/message-model.component';
import {FinanceService} from '@service/services/server/finance.service';
import {DepositFinalConsentModalComponent} from '@core/_shared/shared-components/deposit-final-consent-modal/deposit-final-consent-modal.component';
import {environment} from '@env/environment';
import {AdditionalInformationComponent} from '@core/_shared/shared-components/company-info/additional-information/additional-information.component';
import {AdditionalOwnersComponent} from '@core/_shared/shared-components/company-info/additional-owners/additional-owners.component';
import {ControllingManagerComponent} from '@core/_shared/shared-components/company-info/controlling-manager/controlling-manager.component';
import {SharedService} from '@core/util-services/shared.service';
import {ContentService} from '../../constants/content.service';
import {CustomQuestionsComponent} from '@shared/review/custom-questions/custom-questions.component';
import {NotificationService} from '@core/util-services/notification/notification.service';
import {getInstitutionId} from '../../constants/constant';
import {SpinnerDialogService} from '../../core/_shared/spinner-dialog/spinner-dialog.service';
import {ProvisionService} from '@service/services/server/provision.service';
import {RadioOptionModel} from '@shared/form-components/radio-group/radio-option.model';
import {CommonFinanceService} from '@service/services/common-services/common-finance.service';
import {MissingFieldComponent} from '@shared/form-components/missing-field/missing-field.component';

@Component({
    selector: 'm-review',
    templateUrl: './review.component.html',
    styleUrls: ['./review.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [UpperCasePipe]
})
export class ReviewComponent implements OnInit, OnDestroy {
    stepper: MatStepper;
    private stepperStepsSubscription: Subscription;

    @ViewChild(MatStepper) set stepperContent(stepper: MatStepper) {
        this.stepper = stepper;
        if (stepper) {
            // This setter (and steps.changes) runs mid change detection. Deciding there, flips finalSubmitModalOpened
            // after the page was already rendered in the same pass, so the title stays painted behind the popup
            // until some later tick. A microtask lets the check run right after, still before the browser paints.
            this.stepperStepsSubscription?.unsubscribe();
            this.stepperStepsSubscription = stepper.steps.changes.subscribe(() => Promise.resolve().then(() => this.maybeSkipToFinalSubmit()));
            Promise.resolve().then(() => this.maybeSkipToFinalSubmit());
        }
    }

    institutionSettings = getInstitutionId(environment.env);
    cssWidthClass = this.institutionSettings.singleColumnLayout ? 'col-sm-12' : 'col-md-6';
    @ViewChildren(CustomQuestionsComponent)
    customQuestionsCmps!: QueryList<CustomQuestionsComponent>;
    appConfig: any;
    keyIdentifier: KeyIdentifierModel;
    loadingSubject = new BehaviorSubject<boolean>(false);
    loading$ = this.loadingSubject.asObservable();
    loadingCompleted: boolean;
    minDate = new Date();
    maxDate = new Date();
    application: ApplicationModel;
    finance: FinanceModel;
    applicant: ApplicantModel;
    coborrowerApplicant: ApplicantModel = new ApplicantModel();
    referenceApplicant: ReferenceApplicantModel = new ReferenceApplicantModel();
    companyApplicant: ApplicantModel;
    controllingManager: ApplicantModel;
    otherOwners: ApplicantModel[];
    beneficiaryApplicants: ApplicantModel[];
    employment: EmploymentModel = new EmploymentModel();
    residence: ResidenceModel = new ResidenceModel();
    consent: ConsentModel = new ConsentModel();
    phonePin: number;
    authorizedUserRequired: boolean = false;
    survivorshipBeneficiary: boolean = false;
    private authorizedSignerSkipLogged = false;
    finalSubmitPageConfig: any = {};
    miscellaneousFieldsConfig: any;
    finalSubmitPages: any;
    beneficiaryType: any;
    currentStep: number = 0;
    subscription: Subscription;
    subscriptionOtherOwner: Subscription;
    subscriptionControllingManager: Subscription;
    step1ButtonOptions: ProgressButtonOptions = {
        active: false,
        text: 'Next',
        buttonColor: 'primary',
        // @ts-ignore
        flat: true,
        stroked: false,
        disabled: false
    };
    financeSubscription: Subscription;
    showConsent1: boolean = false;
    errorTransaction: boolean = false;
    consentsConfig: any;
    newApp: ApplicationModel;
    showConsent3: boolean = false;
    declarationQuestions: any = [];
    assetsQuestions: any = [];
    customQuestions: any = [];
    showAdditionalQuestions: boolean = false;
    showCoBoAdditionalQuestions: boolean = false;
    percentageError: boolean = false;
    radioArray: RadioOptionModel = new RadioOptionModel();
    selectedCheckingAccountList = [];
    selectedSubProducts = [];
    selectedQuestionsConfig = [];
    displayConsents = [];
    selectedConsents: number[] = [];
    showErrors: boolean = false;
    /**
     * Derived rather than assigned. The authorized user (parent/guardian) mat-step is gated on
     * !isYouthKidAccount, and initializeApplicant() reads isYouthAccount - both run before the awaits
     * in ngOnInit finish. Assigning these after those awaits let the step render while the flag was
     * still undefined and then destroyed it once the flag flipped, which leaves the stepper on the
     * same selectedIndex now occupied by a different step, as if Next had been pressed.
     */
    get isYouthAccount(): boolean {
        return !!this.keyIdentifier.loanPurposeConfig.title.toLowerCase().includes('youth');
    }

    get isYouthKidAccount(): boolean {
        const loanPurposeConfig = this.keyIdentifier.loanPurposeConfig;
        return this.isYouthAccount && loanPurposeConfig.minAge === 1 && loanPurposeConfig.maxAge === 12;
    }
    finalSubmitText = '';
    // set once ngOnInit has finished resolving every step's visibility flag
    stepVisibilityResolved = false;
    finalSubmitModalOpened: boolean = false;
    private additionalInfoCmp: AdditionalInfoComponent;
    private identityInfoCmp: IdentityInfoComponent;
    private referenceApplicantCmp: ReferenceApplicantComponent;
    private employmentInfoCmp: EmploymentInfoComponent;
    private residenceInfoCmp: ResidenceInfoComponent;
    private authorizedUserCmp: AuthorizedUserComponent;
    private beneficiaryCmp: BeneficiaryComponent;
    private companyAdditionalInfoCmp: AdditionalInformationComponent;
    private companyAdditionalOwnersCmp: AdditionalOwnersComponent;
    private companyControllingManagerCmp: ControllingManagerComponent;

    /**
     * True only when the co-applicant was handed over from the /consent page to fill the additional
     * information step here. Their consent is taken by the final consent modal at the end of this page,
     * so the primary consent step is hidden for this hand over and only for this hand over.
     */
    get isCoApplicantOnReviewPage(): boolean {
        return !!this.keyIdentifier?.isCoBoConsentFlow
            && this.sharedKeyDataService.isReviewPageRequiredForCoApplicant(this.keyIdentifier);
    }

    // Mirrors the six section conditions inside the Additional Information step in review.component.html.
    // With none of them configured that step renders as a blank page carrying only the submit button.
    get hasAdditionalInfoSections(): boolean {
        const misc = this.miscellaneousFieldsConfig;
        const isCoBoFlow = this.keyIdentifier?.isCoBoConsentFlow;
        return (!isCoBoFlow && !!(misc?.mothersMaidenName || misc?.last4ssn || misc?.homePhone || misc?.workPhone || misc?.ssn))
            || !!this.finalSubmitPageConfig?.collectID
            || (!isCoBoFlow && !!(this.appConfig?.finalSubmitPage?.grossAnnualIncome || this.appConfig?.finalSubmitPage?.rentMortgagePmt))
            || (!isCoBoFlow && !!this.appConfig?.phonePin)
            || !!this.finalSubmitPageConfig?.employmentHistory
            || !!(this.finalSubmitPageConfig?.residencyHistory || this.appConfig?.mailingAddress);
    }

    // Additional info step is the final scenario
    get showSubmitOnAdditionalInfoStep(): boolean {
        if (this.isCoApplicantOnReviewPage) {
            // Authorized user, beneficiary, reference and the primary consent steps are all hidden for a
            // handed over co-applicant, so the co-applicant questions step is the only one that can
            // follow this step.
            return !(this.showAdditionalQuestions && this.showCoBoAdditionalQuestions);
        }
        const hasCustomQuestionsStep = this.keyIdentifier?.isCoBoConsentFlow
            ? this.showAdditionalQuestions && this.showCoBoAdditionalQuestions
            : this.showAdditionalQuestions;
        return !this.finalSubmitPageConfig?.authorizedUser && !this.isBeneficiaryStepVisible && !hasCustomQuestionsStep;
    }

    // authorizedUser is visible and last step
    get showSubmitOnAuthorizedUserStep(): boolean {
        return !!this.finalSubmitPageConfig?.authorizedUser && !this.isBeneficiaryStepVisible && !this.showAdditionalQuestions;
    }

    // beneficiary checker
    get isBeneficiaryStepVisible(): boolean {
        return !!this.appConfig?.survivorshipBeneficiary && this.finalSubmitPageConfig?.survivorshipBeneficiary !== false;
    }
     // beneficiary User is visible and last step and no authorizedUser
    get showSubmitOnBeneficiaryStep(): boolean {
        return this.isBeneficiaryStepVisible && !this.finalSubmitPageConfig?.authorizedUser && !this.showAdditionalQuestions;
    }

    get occupationCollected(): boolean {
        const addIncomePage = this.appConfig?.addIncomePage;
        return addIncomePage?.enabled !== false
            && addIncomePage?.askOccupation !== false;
    }

    getButtonOptions(isSubmitStep: boolean) {
        return isSubmitStep ? { ...this.step1ButtonOptions, text: 'Submit Application' } : this.step1ButtonOptions;
    }

    constructor(
        private elRef: ElementRef,
        private router: Router,
        private location: Location,
        private titleService: Title,
        private datePipe: DatePipe,
        private uppercasePipe: UpperCasePipe,
        private matDialog: MatDialog,
        public contentService: ContentService,
        private sharedKeyDataService: SharedkeyDataService,
        private applicationService: ApplicationService,
        private applicantService: ApplicantService,
        private financeService: FinanceService,
        private cdr: ChangeDetectorRef,
        private sharedService: SharedService,
        private provisionService: ProvisionService,
        private notificationService: NotificationService,
        private spinnerDialog: SpinnerDialogService,
        private commonFinanceService: CommonFinanceService
    ) {
        this.appConfig = AppConfigService.config;
        this.sharedService.setStepper(1.5);
        this.subscription = sharedKeyDataService.keyIdentifier$
            .subscribe((data) => {
                this.keyIdentifier = data;

                // if (environment.debug) {
                //      this.keyIdentifier.applicationId = 80694;
                //      this.keyIdentifier.loanPurposeConfig = AppConfigService.config.loanPurpose['SAV'];
                // }
                if (this.keyIdentifier.loanPurposeConfig) {
                    // TODO: remove backward compatability after moving to S3
                    this.finalSubmitPageConfig = this.keyIdentifier.loanPurposeConfig.finalSubmitPages?.page1 || this.keyIdentifier.loanPurposeConfig.finalSubmitPages;
                    this.miscellaneousFieldsConfig = this.keyIdentifier.loanPurposeConfig.finalSubmitPages.miscellaneousFields;
                    // this.finalSubmitPageCount = this.keyIdentifier.loanPurposeConfig.finalSubmitPages.pageCount;
                    // this.currentPage = 1;
                    // this.barButtonOptions.text = (this.finalSubmitPageCount === this.currentPage ? 'Submit Application' : 'Continue');

                    this.finalSubmitPages = this.keyIdentifier.loanPurposeConfig.finalSubmitPages;
                }
            });
        this.loading$.subscribe((data) => {
            this.loadingCompleted = data;
            if (data) {
                this.spinnerDialog.show('Loading available options...');
            } else if (this.stepVisibilityResolved) {
                this.spinnerDialog.close();
            }
        });
        if (this.appConfig && this.appConfig.beneficiaryType) {
            this.beneficiaryType = Object.values(this.appConfig.beneficiaryType);
        } else {
            this.beneficiaryType = Object.values(['Child', 'Friend', 'Grandchild', 'Parent', 'Other', 'Sibling', 'Spouse']);
        }

        if (this.appConfig?.consentsConfigDeposit) {
            const consents = this.appConfig?.consentsConfigDeposit[(this.keyIdentifier?.channel || 'web').toLowerCase()].finalSubmit;
            consents.forEach(consent => {
                if (consent.text) {
                    this.displayConsents.push(consent);
                }
            });
        }
        this.consentsConfig = this.appConfig?.consentsConfig;
    }

    @ViewChild(AdditionalInfoComponent, {static: false}) set additionalInfoContent(content: AdditionalInfoComponent) {
        if (content) {
            this.additionalInfoCmp = content;
        }
    }

    @ViewChild(IdentityInfoComponent, {static: false}) set identityInfoContent(content: IdentityInfoComponent) {
        if (content) {
            this.identityInfoCmp = content;
        }
    }

    @ViewChild(ReferenceApplicantComponent, {static: false}) set referenceContent(content: ReferenceApplicantComponent) {
        if (content) {
            this.referenceApplicantCmp = content;
        }
    }

    @ViewChild(EmploymentInfoComponent, {static: false}) set employmentContent(content: EmploymentInfoComponent) {
        if (content) {
            this.employmentInfoCmp = content;
        }
    }

    @ViewChild(ResidenceInfoComponent, {static: false}) set residenceContent(content: ResidenceInfoComponent) {
        if (content) {
            this.residenceInfoCmp = content;
        }
    }

    @ViewChild(AuthorizedUserComponent, {static: false}) set authorizedUserContent(content: AuthorizedUserComponent) {
        if (content) {
            this.authorizedUserCmp = content;
        }
    }

    @ViewChild(BeneficiaryComponent, {static: false}) set beneficiaryContent(content: BeneficiaryComponent) {
        if (content) {
            this.beneficiaryCmp = content;
        }
    }

    @ViewChild(AdditionalInformationComponent, {static: false}) set additionalCompanyInfoContent(content: AdditionalInformationComponent) {
        if (content) {
            this.companyAdditionalInfoCmp = content;
        }
    }

    @ViewChild(AdditionalOwnersComponent, {static: false}) set additionalOwnersContent(content: AdditionalOwnersComponent) {
        if (content) {
            this.companyAdditionalOwnersCmp = content;
        }
    }

    @ViewChild(ControllingManagerComponent, {static: false}) set companyControllingManager(content: ControllingManagerComponent) {
        if (content) {
            this.companyControllingManagerCmp = content;
        }
    }

    async ngOnInit() {
        this.appConfig = AppConfigService.config;
        this.titleService.setTitle('Review and Submit your account application');

        this.maxDate.setFullYear(this.maxDate.getFullYear() -
            ((this.keyIdentifier.loanPurposeConfig && (this.keyIdentifier.loanPurposeConfig.minAge >= 0)) ?
                this.keyIdentifier.loanPurposeConfig.minAge : 18));
        this.minDate.setFullYear(this.minDate.getFullYear() -
            ((this.keyIdentifier.loanPurposeConfig && (this.keyIdentifier.loanPurposeConfig.maxAge >= 0)) ?
                this.keyIdentifier.loanPurposeConfig.maxAge : 85));

        this.loadingSubject.next(true);

        let doContinue: boolean = true;
        if (!this.sharedKeyDataService.isIdentifierAnnounced(this.keyIdentifier)) {
            doContinue = false;
        } else if (this.keyIdentifier.applicationId === undefined || this.keyIdentifier.applicationId === null
            || this.keyIdentifier.applicationId <= 0) {
            doContinue = false;
        }
        if (!doContinue) {
            console.log('something wrong with keyidentifier, hence redirecting to reentry screen =>',
                this.keyIdentifier);
            this.router.navigate(['/myapp'], {queryParams: {redirect: 'review'}});
            this.loadingSubject.next(false);
        } else {
            console.log('Review Page ngOnInit => ', this.keyIdentifier, new Date().getTime());
            this.load();
        }
        try {
            await this.updateApplicationAndKeyIdentifier(this.keyIdentifier);

            if (!this.keyIdentifier.isCoBoConsentFlow) {
                await this.getProducts();
            }
            this.showAdditionalQuestions = this.hasQuestionsConfig();
            this.showCoBoAdditionalQuestions = this.hasQuestionsConfigForApplicant();
        } finally {
            // showAdditionalQuestions/showCoBoAdditionalQuestions are only settled here, after the awaits, while
            // load() above was fired before them - so the stepper can have rendered against stale flags and be
            // reporting zero steps. The loader is held until now so an empty review page never flashes; the
            // finally means a rejected getProducts() still releases it. Flush the view before deciding.
            this.stepVisibilityResolved = true;
            this.cdr.detectChanges();
            this.maybeSkipToFinalSubmit();
            if (!this.loadingCompleted) {
                this.spinnerDialog.close();
            }
        }
    }

    onConsentChange(isChecked: boolean, index: number): void {
        if (isChecked) {
            // Add the index if checked
            if (!this.selectedConsents.includes(index)) {
                this.selectedConsents.push(index);
            }
        } else {
            // Remove the index if unchecked
            this.selectedConsents = this.selectedConsents.filter(i => i !== index);
            const consent = this.displayConsents[index];
            if (consent?.required) {
                // Show validation immediately
                this.showErrors = true;
            }
        }
    }

    load() {
        this.applicationService.findApplication(this.keyIdentifier.applicationId).subscribe(result => {
            this.initializeApplication(result);
        });
    }

    beneficiaryPercentageError(data: boolean) {
        this.percentageError = data;
    }

    goToPrevStep(event) {
        this.step1ButtonOptions.disabled = false;
        if (this.stepper.selectedIndex === this.stepper.steps.length - 1) {
            this.step1ButtonOptions.text = 'Next';
        }
        if (this.stepper.selectedIndex === 0) {
            this.location.back();
        } else {
            this.stepper.previous();
        }
    }

    initializeApplication(res): void {
        this.application = res;
        this.keyIdentifier.updateKeyIdentifier(res);

        this.newApp = new ApplicationModel();

        if (!this.application) {
            this.router.navigate(['/myapp'], {queryParams: {redirect: 'review'}});
        } else {
            this.initializeFinance();
            this.initializeApplicant();
        }
        if (this.keyIdentifier.loanPurposeConfig && this.keyIdentifier.loanPurposeConfig.hardInquiryConsentRequired) {
            this.consent = new ConsentModel();
        }
        this.loadingSubject.next(false);
    }

    initializeApplicant() {
        if (!this.application.applicants || this.application.applicants.length === 0) {
            this.applicant = new ApplicantModel();
        } else {
            const applicantType = this.keyIdentifier.isCoBoConsentFlow ? ApplicantType.COBORROWER : ApplicantType.PRIMARY;
            this.applicant = this.application.applicants.find(apl => apl.applicantType === applicantType);

            // to set gross annual income
            console.log('appConfig >>>>>>', this.appConfig);
            if (this.appConfig.finalSubmitPage.grossAnnualIncome && this.applicant.employers && this.applicant.employers.length > 0) {
                this.employment = this.applicant.employers[0];
                this.employment.salary = (this.employment.schedule === 'MONTHLY') ?
                    this.applicant.employers[0].salary * 12 :
                    this.applicant.employers[0].salary;
                this.employment.salaryString = this.employment.salary.toString();
                this.employment.schedule = 'ANNUALLY';
            }
            // to set gross annual income
            if (this.appConfig.finalSubmitPage.rentMortgagePmt && this.applicant.residence) {
                this.residence = this.applicant.residence;
                if(this.applicant.residence.cost && this.applicant.residence.cost.toString() === "0" && !this.applicant.residence.status){
                this.residence.cost = null;
                }
            }

            if (this.application.applicants.length > 1) {

                if (this.keyIdentifier.isCoBoConsentFlow) {
                    this.coborrowerApplicant = this.applicant;
                }

                if (this.finalSubmitPageConfig.authorizedUser && !this.keyIdentifier.isCoBoConsentFlow) {
                    this.coborrowerApplicant = this.application.applicants.find(apl => apl.applicantType === ApplicantType.COBORROWER) || new ApplicantModel();

                    // "this.coborrowerApplicant.applicantType === 'S'" this condition is because we are initializing with type 'P'
                    // and then while posting we will replace the type to 'S'
                    if (this.coborrowerApplicant && this.coborrowerApplicant.applicantType === ApplicantType.COBORROWER) {
                        this.authorizedUserRequired = true;
                    }
                }

                if (this.keyIdentifier.loanPurposeConfig.isBusinessProduct) {
                    this.companyApplicant = this.application.applicants.find(apl => apl.applicantType === ApplicantType.BUSINESS) || new ApplicantModel();
                    this.controllingManager = this.application.applicants.find(apl =>
                        apl.applicantType === ApplicantType.CONTROLLINGMANAGER) || new ApplicantModel();
                    this.otherOwners = this.application.applicants.filter(apl => apl.applicantType === ApplicantType.OTHER);
                    this.financeService.otherOwners$.next(this.otherOwners);

                    this.coborrowerApplicant.dateOfBirth = this.coborrowerApplicant.dateOfBirth ? new Date(this.coborrowerApplicant.dateOfBirth) : null;
                    this.subscriptionOtherOwner = this.financeService.otherOwners$.subscribe(otherOwners => {
                        this.otherOwners = otherOwners;
                    });

                    this.subscriptionControllingManager = this.financeService.controllingManager$.subscribe(item => {
                        this.controllingManager = item;
                    });
                }
            }

            if (this.appConfig.survivorshipBeneficiary) {
                this.beneficiaryApplicants = this.application.applicants.filter(apl => apl.applicantType === ApplicantType.BENEFICIARY);
                this.survivorshipBeneficiary = this.beneficiaryApplicants.length > 0;

                if (this.beneficiaryApplicants.length <= 0) {
                    const beneficiaryApplicant = new ApplicantModel();
                    beneficiaryApplicant.applicantType = ApplicantType.BENEFICIARY;
                    this.beneficiaryApplicants.push(beneficiaryApplicant);
                }
            }

        }

        if (this.keyIdentifier.isGuardianRequired || this.isYouthAccount) {
            this.authorizedUserRequired = true;
        }

        this.keyIdentifier.application = this.application;
        this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);
        console.log('APPLICANT >>>>>>>>>>>>>>>>>', this.applicant);
        this.cdr.detectChanges();
    }

    initializeFinance() {
        this.finance = new FinanceModel();
        this.finance.amountFinance = this.application.loanAmount || 0;
        this.finance.term = this.application.term;
        this.finance.apr = this.application.apr;
        this.finance.totalMonthlyPayment = this.application.emi;
        this.finance.isAutoPayment = (this.application.ach === undefined || this.application.ach === null) ? false : this.application.ach;
    }

    getProducts() {
        return new Promise<any>((resolve, reject) => {
            this.applicationService.getSelectedProducts(this.keyIdentifier.applicationId).subscribe(async (resp) => {
                if (resp.code === 200 && resp.offers?.length) {
                    this.selectedSubProducts = resp.offers
                        .filter(offer => offer.selectedIndicator)
                        .map(offer => ({
                            subProductName: offer.subProductName,
                            subProductSubType: offer.subProductSubType
                        }));

                    this.cdr.detectChanges();
                }
                resolve(true);
            }, () => {
                reject();
            });
        });
    }


    hasQuestionsConfig(): boolean {
        return (
            this.appConfig.questionsConfig ||
            this.keyIdentifier.loanPurposeConfig?.questionsConfig.customQuestions.length > 0  ||
            (this.selectedSubProducts.length > 0 &&
                this.selectedSubProducts.some(subProduct =>
                    Boolean(this.appConfig.loanPurpose[subProduct.subProductName]?.questionsConfig.customQuestions.length > 0 )
                ))
        );
    }

    hasQuestionsConfigForApplicant(): boolean {
		return (this.keyIdentifier.loanPurposeConfig?.questionsConfig?.customQuestions || []).some(question => question.visibility === 'APPLICANT');
	}

    async onSubmit(step) {
        if (this.step1ButtonOptions.active) {
            return;
        }
        try {
            this.step1ButtonOptions.active = true;
            switch (step) {
                case 1:
                    await this.processPrimaryApplicantData();
                    break;
                case 2:
                    await this.processAuthorizedUser();
                    break;
                case 3:
                    await this.processBeneficiaries();
                    break;
                case 4:
                    await this.processOtherApplicants();
                    break;
                case 5:
                    await this.processCompanyApplicant();
                    break;
                case 6:
                    await this.processCompanyAdditionalOwners();
                    break;
                case 7:
                    await this.processCompanyControllingManager();
                    break;
                case 8:
                case 9:
                    const index = this.keyIdentifier.isCoBoConsentFlow ? step - 9 : step - 8;
                    const targetCmp = this.customQuestionsCmps.toArray()[index];
                    await this.processCustomQuestions(targetCmp);
                    break;
            }
            this.goToNextStep(step);
        } catch (err) {
            if (err.code && err.code.toString().startsWith('4')) {
                const dialogRefAlert = this.matDialog.open(MessageModelComponent, {
                    width: '600px',
                    data: {
                        type: 'warn',
                        showCloseButton: true,
                        message: err.message
                    }
                });
                dialogRefAlert.afterClosed().subscribe(resAlert => {

                });
            }
            this.step1ButtonOptions.active = false;
            this.cdr.detectChanges();

        }

    }

    processPrimaryApplicantData() {
        try {
            const checkValids = [];
            if (this.additionalInfoCmp && this.additionalInfoCmp.f) {
                this.additionalInfoCmp.runValidation();
                checkValids.push(this.additionalInfoCmp.f.form.valid);
            }

            if (this.identityInfoCmp && this.identityInfoCmp.f) {
                this.identityInfoCmp.runValidation();
                checkValids.push(this.identityInfoCmp.f.form.valid);
            }

            if (this.finalSubmitPageConfig?.employmentHistory && this.employmentInfoCmp) {
                this.employmentInfoCmp.runValidation();
                checkValids.push(this.employmentInfoCmp.employeeForm.valid);
            }

            if ((this.finalSubmitPageConfig?.residencyHistory || this.appConfig.mailingAddress) && this.residenceInfoCmp) {
                this.residenceInfoCmp.runValidation();
                checkValids.push(this.residenceInfoCmp.residenceForm.valid);
                if (this.appConfig.mailingAddress) {
                    checkValids.push(this.residenceInfoCmp.mailingAddressForm.valid);
                }
            }
            this.cdr.detectChanges();
            // return if anyone form is invalid
            if (checkValids.filter(valid => !valid).length > 0) {
                return Promise.reject('Invalid data');
            } else {
                return this.updatePrimaryApplicant();
            }
        } catch (e) {
            console.log(e);
            return Promise.reject(e);
        }

    }

    updatePrimaryApplicant() {

        return new Promise((resolve, reject) => {
            if (this.finalSubmitPageConfig.employmentHistory && this.employmentInfoCmp) {
                const employeeFormValues = this.employmentInfoCmp.employeeForm.getRawValue();
                if (employeeFormValues && employeeFormValues.employeeAdd && employeeFormValues.employeeAdd.length > 0) {
                    const employmentAdd = [];
                    employeeFormValues.employeeAdd.forEach((value, index) => {
                        // add salary for first entry of employment
                        if (index === 0 && this.appConfig.finalSubmitPage.grossAnnualIncome && this.employment.salaryString) {
                            value.salary = this.employment.salaryString.replace(/[^0-9 ]/g, '');
                            value.schedule = 'ANNUALLY';
                        }
                        employmentAdd.push({
                            ...(value.employmentId ? {employmentId: value.employmentId} : {}),
                            empType: value.employeeType,
                            length: value.employmentLength,
                            empName: this.employmentInfoCmp.getEmployerName(value.employeeType, value.employerName),
                            salary: value.salary,
                            schedule: value.schedule || 'MONTHLY',
                            position: value.position,
                            empStatus: value.empStatus || 'P'
                        });
                    });
                    this.applicant.employers = employmentAdd;
                }

            }

            // to set gross annual income
            if (this.appConfig.finalSubmitPage.rentMortgagePmt && this.residence && !this.keyIdentifier.isCoBoConsentFlow) {
                this.applicant.residence = this.residence;
            }

            if (this.finalSubmitPageConfig.residencyHistory && this.residenceInfoCmp) {
                const residenceAdd = [];
                const address = [];
                const residenceFormValues = this.residenceInfoCmp.residenceForm.getRawValue();
                if (residenceFormValues && residenceFormValues.residenceAdd && residenceFormValues.residenceAdd.length > 0) {
                    residenceFormValues.residenceAdd.forEach(value => {
                        residenceAdd.push({
                            length: +(value.residenceLength || 0),
                            status: value.residenceStatus,
                            residenceId: value.residenceId,
                        });

                        address.push({
                            streetAddress: value.streetAddress,
                            addressLine2: value.addressData.addressLine2 || '',
                            zipCode: value.zipCode,
                            city: value.city,
                            state: value.state,
                            county: value.county,
                            country: value.country
                        });
                    });
                    if (residenceAdd[0] && address[0]) {
                        this.applicant.residence = {...this.applicant.residence, ...residenceAdd[0]};
                        this.applicant.address = {...this.applicant.address, ...address[0]};
                    }
                    // delete first address and residence to use part residences
                    residenceAdd.shift();
                    address.shift();
                    const pastResidences = [];
                    address.forEach((ad, i) => {
                        pastResidences.push({
                            ...(residenceAdd[i].residenceId ? {residenceId: residenceAdd[i].residenceId} : {}),
                            status: residenceAdd[i].status,
                            length: residenceAdd[i].length,
                            applicationId: this.keyIdentifier.applicationId,
                            address: {
                                streetAddress: ad.streetAddress,
                                addressLine2: ad.addressLine2 || '',
                                city: ad.city,
                                state: ad.state,
                                zipCode: ad.zipCode,
                                county: ad.county,
                                country: ad.country,
                                applicationId: this.keyIdentifier.applicationId
                            }
                        });
                    });

                    this.applicant.pastResidenceDetails = pastResidences;
                }
            }

            if (this.appConfig.mailingAddress && this.residenceInfoCmp) {
                let mailingAddressValues: any;
                if (!this.residenceInfoCmp.sameMailAddress) {
                    mailingAddressValues = this.residenceInfoCmp.mailingAddressData;
                } else {
                    mailingAddressValues = this.applicant.address;
                }

                const mailingAddress: AddressModel = {
                    streetAddress: mailingAddressValues.streetAddress,
                    addressLine2: mailingAddressValues.addressLine2 || '',
                    city: mailingAddressValues.city,
                    state: mailingAddressValues.state,
                    zipCode: mailingAddressValues.zipCode,
                    county: mailingAddressValues.county,
                    country: mailingAddressValues.country,
                    addressUsage: 'COMMUNICATION',
                    ...(mailingAddressValues.addressId && mailingAddressValues.addressUsage === 'COMMUNICATION' && {
                        addressId: mailingAddressValues.addressId
                    })
                };

                const mailingAddressIndex = this.applicant.additionalAddressDetails.findIndex(add => add.addressId === mailingAddressValues.addressId);
                if (mailingAddressIndex > -1) {
                    this.applicant.additionalAddressDetails[mailingAddressIndex] = mailingAddress;
                } else {
                    this.applicant.additionalAddressDetails.push(mailingAddress);
                }
            }

            if (this.appConfig.phonePin && this.phonePin) {
                const phonePinConsent = new CustomerConsentModel();
                phonePinConsent.name = ConsentEnum.PHONEPIN;
                phonePinConsent.value = this.phonePin;
                this.applicationService.addConsent(this.keyIdentifier.applicationId, this.applicant.applicantId, phonePinConsent).subscribe(value => {
                });
            }

            let applicantObj: ApplicantModel;
            applicantObj = {...this.applicant};

            applicantObj.idIssueDate = this.datePipe.transform(this.applicant.idIssueDate, 'MM/dd/yyyy');
            applicantObj.idExpDate = this.datePipe.transform(this.applicant.idExpDate, 'MM/dd/yyyy');
            let updateObservable: Observable<any>;
            if (this.keyIdentifier.isCoBoConsentFlow) {
                updateObservable = this.applicantService.updateCoApplicant(this.keyIdentifier.applicationId, applicantObj);
            } else {
                updateObservable = this.applicantService.updateApplicant(this.keyIdentifier.applicationId, applicantObj);
            }
            updateObservable.subscribe({
                next: async (resp) => {
                    resolve(true);
                },
                error: err => reject(err)
            });
        });

    }

    processAuthorizedUser() {
        return new Promise((resolve, reject) => {
            if (!this.authorizedUserRequired) {
                if (!this.authorizedSignerSkipLogged) {
                    this.authorizedSignerSkipLogged = true;
                    const skipNote = this.keyIdentifier.loanPurposeConfig.isBusinessProduct
                        ? 'Authorized Signer Skipped'
                        : 'Joint Owner Skipped';
                    this.applicationService.addApplicationEvent(this.keyIdentifier.applicationId, skipNote)
                        .subscribe({error: (error) => console.error(error)});
                }
                resolve(true);
                return;
            }
            this.authorizedSignerSkipLogged = false;
            let isValid = false;
            if (this.authorizedUserCmp) {
                isValid = this.authorizedUserCmp.runValidation();
            }

            if (isValid) {
                this.step1ButtonOptions.active = true;
                // this will update coborrower Applicant object
                this.coborrowerApplicant = this.authorizedUserCmp.getApplicantDetails();

                if (this.coborrowerApplicant.primaryPhone) {
                    this.coborrowerApplicant.primaryPhone = this.coborrowerApplicant.primaryPhone.toString().replace(/([\s,.€()_-])/g, '');
                }

                this.coborrowerApplicant.dob = this.datePipe.transform(this.coborrowerApplicant.dateOfBirth, 'MM/dd/yyyy');
                this.coborrowerApplicant.idIssueDate = this.datePipe.transform(this.coborrowerApplicant.idIssueDate, 'MM/dd/yyyy');
                this.coborrowerApplicant.idExpDate = this.datePipe.transform(this.coborrowerApplicant.idExpDate, 'MM/dd/yyyy');
                this.coborrowerApplicant.firstName = this.uppercasePipe.transform(this.coborrowerApplicant.firstName.trim());
                this.coborrowerApplicant.middleName = this.coborrowerApplicant.middleName ?
                    this.uppercasePipe.transform(this.coborrowerApplicant.middleName.trim()) : null;
                this.coborrowerApplicant.lastName = this.uppercasePipe.transform(this.coborrowerApplicant.lastName.trim());
                this.applicantService.addApplicant(this.keyIdentifier.applicationId, this.coborrowerApplicant).subscribe(async (resp) => {
                    this.coborrowerApplicant.applicantId = resp.id;
                    resolve(true);
                }, err => {
                    console.log(err);
                    reject(err);
                });

            } else {
                setTimeout(() => {
                    const ssnField = this.elRef.nativeElement.querySelector('creditsnap-ssn[name="coborrowerSsn"]');
                    if (ssnField) {
                        ssnField.scrollIntoView({behavior: 'smooth', block: 'center'});
                        ssnField.focus();
                    } else {
                        this.scrollToField();
                    }
                }, 100);
                reject('Invalid data');
            }
        });
    }

    scrollToField() {
        const invalid = this.elRef.nativeElement.querySelector('.ng-invalid:not(.m-form)');
        if (invalid) {
            (Array.isArray(invalid) ? invalid[0] : invalid).scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }

    processBeneficiaries() {
        return new Promise((resolve, reject) => {
            if (!this.survivorshipBeneficiary) {
                resolve(true);
                return;
            }
            let isValid = true;
            if (this.beneficiaryCmp) {
                this.beneficiaryCmp.runValidation();
                let percentageOfBenefit: number = 0;
                this.beneficiaryCmp.beneficiaryApplicants.forEach((bene) => {
                    if (bene.percentageOwnership) {
                        percentageOfBenefit += Number(bene.percentageOwnership);
                    }
                });
                isValid = this.appConfig.percentageOfBenefit ? this.beneficiaryCmp._beneficiaryForm.valid && percentageOfBenefit === 100 : this.beneficiaryCmp._beneficiaryForm.valid;
            }
            if (isValid) {
                this.step1ButtonOptions.active = true;
                const beneficiaryApplicants = this.beneficiaryCmp.beneficiaryApplicants;
                console.log(beneficiaryApplicants);
                const beneficiaryPromises: Observable<any>[] = [];
                beneficiaryApplicants.forEach((bene, i) => {
                    bene.applicantType = ApplicantType.BENEFICIARY;
                    bene.dob = this.datePipe.transform(bene.dob, 'MM/dd/yyyy');
                    bene.ssn = bene.ssn ? bene.ssn.toString().replace(/([\s,.€()_-])+/g, '') : null;
                    bene.firstName = this.uppercasePipe.transform(bene.firstName.trim());
                    bene.middleName = bene.middleName ? this.uppercasePipe.transform(bene.middleName.trim()) : null;
                    bene.lastName = this.uppercasePipe.transform(bene.lastName.trim());
                    if (this.appConfig.survivorshipBeneficiaryFields && !this.appConfig.survivorshipBeneficiaryFields.address) {
                        delete bene.address;
                        delete bene.residence;
                    }
                    beneficiaryPromises.push(this.applicantService.addApplicant(this.keyIdentifier.applicationId, bene));
                });

                // Update all beneficiaries
                forkJoin(beneficiaryPromises).subscribe((res: any[]) => {
                    res.forEach((b, i) => beneficiaryApplicants[i].applicantId = b.id);
                    this.beneficiaryApplicants = beneficiaryApplicants;
                    resolve(true);
                }, error => reject);
            } else {
                reject('invalid data');
            }

        });
    }

    processOtherApplicants() {
        return new Promise(async (resolve, reject) => {
            try {
                let isValid = true;
                if (this.finalSubmitPageConfig?.reference && this.referenceApplicantCmp?.f) {
                    this.referenceApplicantCmp.runValidation();
                    isValid = this.referenceApplicantCmp.f?.form.valid;
                }
                if (isValid) {
                    this.referenceApplicant.firstName = this.uppercasePipe.transform(this.referenceApplicant.firstName.trim());
                    this.referenceApplicant.lastName = this.uppercasePipe.transform(this.referenceApplicant.lastName.trim());
                    if (this.finalSubmitPageConfig?.reference && this.referenceApplicantCmp?.f) {
                        await this.updateReference();
                    }
                    resolve(true);
                } else {
                    reject('invalid data');
                }
            } catch (err) {
                reject(err);
            }
        });
    }

    processCompanyApplicant() {
        return new Promise(async (resolve, reject) => {
            try {
                let isValid = true;
                if (this.keyIdentifier.loanPurposeConfig.isBusinessProduct) {
                    this.companyAdditionalInfoCmp.runValidation();
                    isValid = this.companyAdditionalInfoCmp.companyApplicantForm.valid;
                }
                if (isValid) {
                    // the checkbox default ("same as business address") never fires a change event,
                    // so make sure the mailing address is on the payload before saving
                    this.companyAdditionalInfoCmp.syncBusinessMailingAddress(this.companyApplicant);
                    this.applicantService.updateApplicant(this.keyIdentifier.applicationId, this.companyApplicant).subscribe(async (resp) => {
                        this.coborrowerApplicant.applicantId = resp.id;
                        resolve(true);
                    }, err => {
                        console.log(err);
                        reject(err);
                    });
                } else {
                    reject('invalid data');
                }
            } catch (err) {
                reject(err);
            }
        });
    }

    processCompanyAdditionalOwners() {
        return new Promise((resolve, reject) => {
            let isValid = false;
            if (this.keyIdentifier.loanPurposeConfig.isBusinessProduct) {
                this.companyAdditionalOwnersCmp.runValidation();
                isValid = this.companyAdditionalOwnersCmp.ownershipForm.valid && this.companyAdditionalOwnersCmp.certifiedOwnersBox;
            }

            if (isValid) {
                const applicantApiArray = [];

                // Beneficial-owner rows the applicant deleted this session (on a resumed
                // application) get sent to the dedicated delete endpoint here, alongside the
                // owner PUTs below — which already carry each entity's remaining businessOwners.
                this.companyAdditionalOwnersCmp.getRemovedBusinessOwnerIds().forEach(businessOwnerApplicantId => {
                    applicantApiArray.push(
                        this.applicantService.deleteBusinessOwner(businessOwnerApplicantId, this.keyIdentifier.applicationId)
                    );
                });

                if (this.otherOwners.length) {
                    this.otherOwners.forEach(owner => {
                        const applicant = new ApplicantModel();
                        applicant.applicantId = owner.applicantId || undefined;
                        applicant.firstName = this.uppercasePipe.transform(owner.firstName.trim());
                        applicant.lastName = this.uppercasePipe.transform(owner.lastName.trim());
                        applicant.applicantType = ApplicantType.OTHER;
                        applicant.percentageOwnership = owner.percentageOwnership;
                        applicant.companyLegalName = owner.companyLegalName;
                        applicant.title = owner.title;

                        // "No"/"none qualify" decisions aren't sent yet — LP-2378 hasn't defined
                        // how to flag those on the entity server-side.
                        const nestedBeneficialOwners: any[] = (owner as any).beneficialOwners || [];
                        if (owner.companyLegalName && (owner as any).hasQualifyingBeneficialOwner === 'yes' && nestedBeneficialOwners.length) {
                            applicant.businessOwners = nestedBeneficialOwners.map((bo): BusinessOwnerModel => ({
                                // Included only when the row already has one (an existing owner
                                // being edited), so the backend updates that record in place
                                // instead of creating a duplicate. A brand-new row has none yet.
                                applicantId: bo.applicantId || undefined,
                                category: 'INDIVIDUAL',
                                firstName: this.uppercasePipe.transform((bo.firstName || '').trim()),
                                lastName: this.uppercasePipe.transform((bo.lastName || '').trim()),
                                title: bo.title || '',
                                percentageOwnership: bo.percentageOwnership,
                                institutionCode: environment.institutionId
                            }));
                        }

                        applicantApiArray.push(this.applicantService.updateApplicant(this.keyIdentifier.applicationId, applicant));
                    });
                }

                console.log(applicantApiArray);
                forkJoin(applicantApiArray).subscribe(res => {
                    console.log('========= Additional Owners updated =================');

                    this.applicationService.findApplication(this.keyIdentifier.applicationId).subscribe(result => {
                        this.keyIdentifier.application = result;
                        this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);
                    });
                    resolve(true);
                }, (e) => {
                    console.error(e);
                    reject(false);
                });
            } else {
                reject('invalid data');
            }
        });
    }

    processCompanyControllingManager() {
        return new Promise((resolve, reject) => {
            if (this.keyIdentifier.loanPurposeConfig.isBusinessProduct) {
                this.companyControllingManagerCmp.validateAndUpdate().then(() => {
                    resolve(true);
                }, () => {
                    reject('invalid data');
                });
            }
        });
    }

    processCustomQuestions(customQuestionsCmp: CustomQuestionsComponent) {
        return new Promise((resolve, reject) => {
            this.step1ButtonOptions.active = true;
            this.cdr.detectChanges();

            let isValid = true;
            try {
                customQuestionsCmp.validateAndSubmitForm();
                isValid = customQuestionsCmp.customQuestionsFormModel.valid;
                if (!isValid) {
                    reject('Invalid data');
                    return;
                }

                this.updateApplicationAndKeyIdentifier(this.keyIdentifier);
                this.step1ButtonOptions.active = false;
                // this.goToNextStep();
                resolve(true);
            } catch (error) {
                console.error(error);
                this.notificationService.error('Something went wrong!');
                this.step1ButtonOptions.active = false;
                reject(error);
            }
        });
    }

    updateApplicationAndKeyIdentifier(keyIdentifier: KeyIdentifierModel) {
        return new Promise((resolve, reject) => {
            this.applicationService.findApplication(keyIdentifier.applicationId).subscribe({
                next: (res) => {
                    this.keyIdentifier.updateKeyIdentifier(
                        res
                    );
                    keyIdentifier.application = res;
                    this.sharedKeyDataService.announceIdentifierChanges(
                        keyIdentifier
                    );
                    resolve(res);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    goToNextStep(currentStep?: number) {
        const isFinalSubmitStep =
            this.stepper.selectedIndex === this.stepper.steps.length - 1 ||
            this.stepper.steps.length === 0 ||
            (currentStep === 1 && this.showSubmitOnAdditionalInfoStep) ||
            (currentStep === 2 && this.showSubmitOnAuthorizedUserStep) ||
            (currentStep === 3 && this.showSubmitOnBeneficiaryStep);

        if (isFinalSubmitStep) {
            this.openFinalSubmitModal();
        } else {
            this.stepper.next();
            this.load();
            // stepper.next() function will update selected index and update button text for last step.
            if (this.stepper.selectedIndex === this.stepper.steps.length - 1) {
                this.step1ButtonOptions.text = 'Submit Application';
            }
            this.step1ButtonOptions.active = false;
        }
    }

    openFinalSubmitModal(): void {
        if (this.isCoApplicantConsentPending) {
            this.submitCoApplicantFinalConsent();
            return;
        }
        if (this.appConfig.hideAgreementsForCoBorrower && this.keyIdentifier.isCoBoConsentFlow) {
            this.router.navigate(['provision']);
        } else {
            if (this.appConfig.hasOwnProperty('hardInquiryConsentModal') && !this.appConfig.hardInquiryConsentModal) {
                this.router.navigate(['agreement']);
            } else {
                this.matDialog.closeAll();
                this.matDialog.open(DepositFinalConsentModalComponent, {
                    disableClose: true,
                    hasBackdrop: true,
                    width: '600px',
                    autoFocus: '#deposit-final-consent-dialog-title',
                    restoreFocus: true,
                    ariaLabelledBy: 'deposit-final-consent-dialog-title',
                    data: {
                        keyIdentifier: this.keyIdentifier,
                        // No review sections, so this popup opened by itself and there is no page behind it to
                        // go back to - drop the close button rather than let the applicant reach a blank screen.
                        hideCloseButton: this.finalSubmitModalOpened
                    }
                }).afterClosed().subscribe(async (res) => {
                    if (!res) {
                        this.step1ButtonOptions.active = false;
                        this.cdr.detectChanges();
                        return;
                    }
                        this.finalSubmitText = res;

                    this.loadingSubject.next(true);
                    // if (this.keyIdentifier.loanPurposeConfig.depositFinalConsentType === 'modal') {
                    await this.sendHtmlToPdf(res).then(() => {

                        if (!this.keyIdentifier.isCoBoConsentFlow) {
                            this.loadingSubject.next(false);
                            this.processFinalSubmit(this.consent);
                        } else {
                            this.loadingSubject.next(false);
                            this.router.navigate(['/provision']);
                        }

                    }, () => {
                        this.step1ButtonOptions.active = false;
                    });
                    // }
                    this.errorTransaction = false;
                });

            }
        }
    }

    // if the stepper ends up with zero visible mat-steps (e.g. every review section disabled in the loan purpose
    // config), there's nothing to review - go straight to the final submit popup instead of showing a blank page
    maybeSkipToFinalSubmit(): void {
        // stepVisibilityResolved gates this: load() is fired before the awaits in ngOnInit that settle the step
        // visibility flags, so the stepper can render against stale flags and briefly report zero steps.
        if (this.finalSubmitModalOpened || !this.stepVisibilityResolved || !this.stepper || !this.application
            || this.stepper.steps.length > 0) {
            return;
        }
        this.finalSubmitModalOpened = true;
        this.openFinalSubmitModal();
    }

    /**
     * True when the co-applicant reached this page through the /consent page hand over, which means the
     * final consent, consent PDF and fraud check still have to be done here.
     */
    private get isCoApplicantConsentPending(): boolean {
        return this.isCoApplicantOnReviewPage && this.application?.coApplicantConsentProvided !== 'Y';
    }

    private openFinalConsentModal(data: any): Observable<any> {
        this.matDialog.closeAll();
        return this.matDialog.open(DepositFinalConsentModalComponent, {
            disableClose: true,
            hasBackdrop: true,
            width: '600px',
            autoFocus: '#deposit-final-consent-dialog-title',
            restoreFocus: true,
            ariaLabelledBy: 'deposit-final-consent-dialog-title',
            data
        }).afterClosed();
    }

    /**
     * Final submit for a co-applicant handed over from the /consent page. Mirrors what the /consent page
     * does for co-applicants when the additional information step stays there.
     */
    private submitCoApplicantFinalConsent() {
        this.openFinalConsentModal({
            keyIdentifier: this.keyIdentifier,
            showLogo: 'no',
            isCoApplicant: true
        }).subscribe(async (res) => {
            if (!res) {
                this.step1ButtonOptions.active = false;
                this.cdr.detectChanges();
                return;
            }
            this.finalSubmitText = res;
            this.loadingSubject.next(true);
            const consent = new CustomerConsentModel();
            consent.name = ConsentEnum.DepositElectronicComm;
            consent.value = 'Y';
            consent.consentText = this.finalSubmitText || '';
            try {
                await this.sendHtmlToPdf(res);
                const consentResp = await lastValueFrom(
                    this.applicationService.addCoApplicantConsent(this.keyIdentifier.applicationId, [consent]));
                this.keyIdentifier.applicationStatus = consentResp.applicationStatus;
                this.keyIdentifier.losStatus = consentResp.losStatus;
                this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);
            } catch (err) {
                // sendHtmlToPdf already notifies on its own failure
                this.loadingSubject.next(false);
                this.step1ButtonOptions.active = false;
                this.cdr.detectChanges();
                return;
            }
            this.loadingSubject.next(false);
            this.errorTransaction = false;
            this.checkCoApplicantFraud();
        });
    }

    private checkCoApplicantFraud() {
        this.commonFinanceService.checkFraud(true).then(() => {
            this.router.navigate(['/provision']);
        }, () => {
            this.step1ButtonOptions.active = false;
            this.sharedService.showNotification('Something went wrong in checking fraud status', Event.Error);
            this.router.navigate(['/thankyou']);
        });
    }

    sendHtmlToPdf(htmlContent) {

        const body = {
            docName: `FinalConsent`,
            docContent: htmlContent
        };
        return new Promise((resolve, reject) => {
            this.applicationService.postHtmlToPdf(this.keyIdentifier.applicationId, body, this.keyIdentifier.isCoBoConsentFlow).subscribe(resp => {
                resolve(true);
                this.step1ButtonOptions.active = false;
            }, err => {
                this.step1ButtonOptions.active = false;
                this.sharedService.showNotification('Something went wrong in sending consents', Event.Error);
                this.cdr.detectChanges();
                reject(err);
            });
        });
    }

    updateReference() {
        return new Promise((resolve, reject) => {
            this.applicantService.updateReference(this.keyIdentifier.applicationId, this.referenceApplicant).subscribe(res => {
                resolve(true);
            }, error => reject);
        });
    }

    async processFinalSubmit(consentOld: ConsentModel) {
        const consentArr = [];
        let consent = new CustomerConsentModel();
        consent.name = 'DepositElectronicComm';
        consent.value = 'Y';
		consent.consentText = this.finalSubmitText || '';
        consentArr.push(consent);
        // this.applicationService.addConsent(this.keyIdentifier.applicationId, this.applicant.applicantId, consent).subscribe();
        if (this.appConfig?.consentsConfigDeposit) {
            // presented: mirrors the consent mat-step *ngIf in review.component.html; each row also needs consent.text
            const isBusinessProduct = this.keyIdentifier.loanPurposeConfig?.isBusinessProduct;
            const consentStepPresented = (!(this.keyIdentifier.applicationType === 'I' && this.keyIdentifier.preApprovalApplicationId) &&
                    (this.consentsConfig?.finalConsent_individual?.text || this.displayConsents.length > 0) && !isBusinessProduct) ||
                (isBusinessProduct && this.appConfig.business_consent);
            this.displayConsents.forEach((consentConfig, index) => {
                const isSelected = this.selectedConsents.includes(index);
                if (!isSelected && !(consentStepPresented && consentConfig?.text)) {
                    return;
                }
                consent = new CustomerConsentModel();
                consent.name = consentConfig.name;
                consent.value = isSelected ? 'Y' : 'N';
				consent.consentText = this.displayConsents[index]?.text || '';
                consentArr.push(consent);
            });

        } else if (this.consentsConfig.consent3_individual && consentOld.consent3_individual) {
            (this.consentsConfig.consent3_individual.consents || []).map(async (consentName) => {
                consent = new CustomerConsentModel();
                consent.name = consentName;
                consent.value = 'Y';
				consent.consentText = this.consentsConfig.consent3_individual.text || '';
                consentArr.push(consent);


            });
        }
        try {
            await new Promise((resolve, reject) => {
                this.applicationService.addConsents(this.keyIdentifier.applicationId, this.applicant.applicantId, consentArr).subscribe({
                    next: async (value) => {
                        resolve(true);
                    }, error: err => {
                        reject(err);
                    }
                });
            });
        } catch (e) {
            console.log(e);
        }


        await this.finalSubmit();

    }

    async finalSubmit() {
        this.spinnerDialog.show('Your application is in progress');

        this.keyIdentifier.financeData = this.finance;
        this.financeSubscription = this.financeService.oldFinanceSubmit(this.keyIdentifier.applicationId, this.finance)
            .subscribe(async (res: any) => {
                this.spinnerDialog.close();

                // server rejected the submit for missing mandatory fields, nothing was saved or sent
                if (MissingFieldComponent.isMissingFieldsResponse(res)) {
                    const result = await this.commonFinanceService.collectMissingFields(res, this.finance);
                    if (result.error) {
                        this.onFinalSubmitError(result.error);
                        return;
                    }
                    if (!result.response) {
                        // cancelled, stay on review without advancing the application
                        this.loadingSubject.next(false);
                        this.step1ButtonOptions.active = false;
                        this.cdr.detectChanges();
                        return;
                    }
                    res = result.response;
                }

                this.keyIdentifier.applicationStatus = res.applicationStatus;
                this.keyIdentifier.losStatus = res.losStatus;
                // Finance submit call give losStatus same as underwriting status so we can avoid get application status call
                this.keyIdentifier.underWritingStatus = res.losStatus;
                this.loadingSubject.next(false);
                this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);

                // Alternate to EXTERNAL_LOS, INTERNAL_LOS, FORWARD_LENDER
                if (res.losStatus) {
                    // redirect to provision
                    this.router.navigate(['/provision']);
                } else {
                    // next Actions in case there is no provision feature set in DynamoDB
                    this.nextAction();
                }

                // this.completeProgressBarAction();
            }, error => {
                this.onFinalSubmitError(error);
            });
    }

    onFinalSubmitError(error: any) {
        this.spinnerDialog.close();
        if (error.code && error.code.toString().startsWith('4')) {
            const dialogRefAlert = this.matDialog.open(MessageModelComponent, {
                width: '600px',
                data: {
                    type: 'warn',
                    showCloseButton: true,
                    message: error.message
                }
            });
            dialogRefAlert.afterClosed().subscribe(resAlert => {
                console.log(resAlert);
            });
        } else {
            this.keyIdentifier.errorCode = 'FINANCIAL_SUBMITTED_FAILED';
            // this.completeProgressBarAction();
            this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);
            this.router.navigate(['/thankyou']);
        }
    }

    nextAction() {
        const keys = this.sharedKeyDataService.performApplicationNextAction(this.keyIdentifier);
        this.keyIdentifier.errorCode = keys.errorCode;

        this.router.navigate([keys.navigationAction]);
        this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);
        this.loadingSubject.next(false);
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.subscriptionOtherOwner) {
            this.subscriptionOtherOwner.unsubscribe();
        }
        if (this.subscriptionControllingManager) {
            this.subscriptionControllingManager.unsubscribe();
        }
        if (this.stepperStepsSubscription) {
            this.stepperStepsSubscription.unsubscribe();
        }
        this.spinnerDialog.close();
    }
}
